/**
 * Pure thread-model logic for the Collaborate session workspace.
 *
 * The thread is a chronological list of items rebuilt from persisted history
 * (conversation messages + run steps) and mutated live by `agent-stream` SSE
 * envelopes (token deltas, tool activity), run-detail snapshots
 * (clarifications), and human gates (approvals / review gates).
 *
 * Kept free of Angular so it can be unit-tested directly.
 */

export interface ThreadUserMessage {
  type: 'user';
  id: string;
  content: string;
  at: string;
  /**
   * True while a mid-run message is waiting for the next turn slot
   * (Cursor-like queue). Cleared when a matching `user_message` step appears
   * or the run finishes.
   */
  queued?: boolean;
}

export interface ThreadAgentMessage {
  type: 'agent';
  id: string;
  runId?: string;
  agentName?: string;
  content: string;
  reasoning?: string;
  /** True while token deltas are still arriving. */
  streaming: boolean;
  /** Chars of content received so far (stream offset tracking). */
  offset: number;
  /** Chars of reasoning received so far. */
  reasoningOffset: number;
  /** Set when a delta arrived with a gap (missed payloads); resolved by a steps resync. */
  gap?: boolean;
  at: string;
}

export interface ThreadToolCall {
  type: 'tool';
  /** toolCallId  -  stable across stream events and persisted steps. */
  id: string;
  runId?: string;
  agentName?: string;
  toolName: string;
  input: unknown;
  output?: string;
  /** True when the live-stream preview was truncated (full data in run_steps). */
  truncated?: boolean;
  pending: boolean;
  at: string;
}

export interface ThreadMarker {
  type: 'marker';
  id: string;
  label: string;
  icon?: string;
  tone: 'info' | 'success' | 'error' | 'warning';
  at: string;
}

export interface ThreadClarification {
  type: 'clarification';
  id: string;
  runId: string;
  question: string;
  answer?: string;
  status: string;
  at: string;
}

export interface ThreadGate {
  type: 'gate';
  id: string;
  gateKind: 'approval' | 'review';
  runId: string;
  status: 'pending' | 'approved' | 'rejected';
  /** Raw enriched approval / review gate object for the card renderer. */
  data: Record<string, unknown>;
  at: string;
}

export type ThreadItem =
  | ThreadUserMessage
  | ThreadAgentMessage
  | ThreadToolCall
  | ThreadMarker
  | ThreadClarification
  | ThreadGate;

/** Row shape from GET /api/chat/conversations/:id/messages. */
export interface HistoryMessage {
  id: string;
  role: string;
  content: string;
  createdAt: string;
}

/** Row shape from GET /api/runs (subset used here). */
export interface HistoryRun {
  id: string;
  agentId: string;
  status: string;
  output?: string | null;
  error?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Step shape from GET /api/runs/:id/steps. */
export interface HistoryStep {
  id: string;
  runId: string;
  agentId: string;
  agentName?: string;
  index: number;
  kind: string;
  content: Record<string, unknown>;
  createdAt: string;
}

export interface ThreadHistoryInput {
  messages: HistoryMessage[];
  runs: HistoryRun[];
  stepsByRun: Record<string, HistoryStep[]>;
  clarifications?: Array<{
    id: string;
    runId: string;
    question: string;
    answer?: string | null;
    status: string;
    askedAt: string;
  }>;
  /** Name shown for assistant messages that have no run attribution. */
  agentName?: string;
}

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'escalated']);

function ts(value: string | Date | undefined): number {
  if (!value) return 0;
  return new Date(value).getTime();
}

function normalize(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * True when this user message likely started a run (not a mid-run steer).
 * Seeded runs embed the latest message under "[Current request]", so we match
 * that or an exact input equality for legacy single-message starts.
 */
function isLikelyRunStarterMessage(
  message: HistoryMessage,
  runs: HistoryRun[],
): boolean {
  const msgAt = ts(message.createdAt);
  for (const run of runs) {
    const runAt = ts(run.createdAt);
    // Message at/before run start (±2s clock skew) is the starter.
    if (Math.abs(msgAt - runAt) <= 2_000 || (msgAt <= runAt && runAt - msgAt < 60_000)) {
      return true;
    }
  }
  return false;
}

/**
 * Rebuilds the full session thread from persisted history: user/assistant
 * messages interleaved with each run's agent trace (messages, tool calls)
 * and clarifications, ordered chronologically.
 *
 * Assistant `messages` rows that duplicate a persisted step (the final run
 * output is stored in both places) are dropped in favor of the step.
 */
export function buildThreadFromHistory(input: ThreadHistoryInput): ThreadItem[] {
  const items: ThreadItem[] = [];

  // Contents already represented by *rendered* step messages, for deduping
  // assistant rows. Only `message` steps count  -  `final` steps are never
  // rendered as thread items, so deduping against them would drop the final
  // answer entirely.
  const stepMessageContents = new Set<string>();
  for (const steps of Object.values(input.stepsByRun)) {
    for (const step of steps) {
      if (step.kind === 'message') {
        const content = String(step.content['content'] ?? '');
        if (content.trim()) stepMessageContents.add(normalize(content));
      }
    }
  }

  // Message ids already applied into a run's graph context via user_message steps.
  const appliedUserMessageIds = new Set<string>();
  for (const steps of Object.values(input.stepsByRun)) {
    for (const step of steps) {
      if (step.kind === 'user_message') {
        const messageId = String(step.content['messageId'] ?? '');
        if (messageId) appliedUserMessageIds.add(messageId);
      }
    }
  }

  /** Runs that still accept Collaborate steer messages (includes escalated). */
  const openSessionStatuses = new Set([
    'queued',
    'running',
    'waiting_approval',
    'waiting_review',
    'waiting_input',
    'escalated',
  ]);

  for (const message of input.messages) {
    if (message.role === 'user') {
      // Mid-run messages that have not yet been drained show as queued.
      const msgAt = ts(message.createdAt);
      const afterActiveRunStart = input.runs.some(
        (r) => openSessionStatuses.has(r.status) && msgAt > ts(r.createdAt) + 2_000,
      );
      const queued =
        afterActiveRunStart &&
        !appliedUserMessageIds.has(message.id) &&
        !isLikelyRunStarterMessage(message, input.runs);
      items.push({
        type: 'user',
        id: `msg:${message.id}`,
        content: message.content,
        at: message.createdAt,
        queued: queued || undefined,
      });
    } else if (message.role === 'assistant') {
      if (stepMessageContents.has(normalize(message.content))) continue;
      items.push({
        type: 'agent',
        id: `msg:${message.id}`,
        agentName: input.agentName,
        content: message.content,
        streaming: false,
        offset: message.content.length,
        reasoningOffset: 0,
        at: message.createdAt,
      });
    }
  }

  for (const run of input.runs) {
    const steps = input.stepsByRun[run.id] ?? [];
    const pendingTools = new Map<string, ThreadToolCall>();

    for (const step of steps) {
      if (step.kind === 'message') {
        const content = String(step.content['content'] ?? '');
        const reasoning = typeof step.content['reasoning'] === 'string' ? step.content['reasoning'] : undefined;
        if (!content.trim() && !reasoning?.trim()) continue;
        items.push({
          type: 'agent',
          id: `step:${step.id}`,
          runId: step.runId,
          agentName: step.agentName,
          content,
          reasoning,
          streaming: false,
          offset: content.length,
          reasoningOffset: reasoning?.length ?? 0,
          at: step.createdAt,
        });
      } else if (step.kind === 'tool_call') {
        const toolCallId = String(step.content['toolCallId'] ?? step.id);
        const item: ThreadToolCall = {
          type: 'tool',
          id: toolCallId,
          runId: step.runId,
          agentName: step.agentName,
          toolName: String(step.content['toolName'] ?? 'tool'),
          input: step.content['input'],
          pending: true,
          at: step.createdAt,
        };
        pendingTools.set(toolCallId, item);
        items.push(item);
      } else if (step.kind === 'tool_result') {
        const toolCallId = String(step.content['toolCallId'] ?? '');
        const output = String(step.content['output'] ?? '');
        const call = pendingTools.get(toolCallId);
        if (call) {
          call.output = output;
          call.pending = false;
        } else {
          items.push({
            type: 'tool',
            id: toolCallId || `step:${step.id}`,
            runId: step.runId,
            agentName: step.agentName,
            toolName: String(step.content['toolName'] ?? 'tool'),
            input: undefined,
            output,
            pending: false,
            at: step.createdAt,
          });
        }
      }
      // 'final', 'approval', 'plan', 'todo_snapshot', 'user_message' steps are
      // represented elsewhere (assistant message, gate card, user bubble).
    }

    // Legacy runs (before final outputs were persisted as messages): surface
    // the run output when no step or message already carries it.
    const output = run.output?.trim();
    if (
      TERMINAL_STATUSES.has(run.status) &&
      output &&
      !stepMessageContents.has(normalize(output)) &&
      !input.messages.some((m) => m.role === 'assistant' && normalize(m.content) === normalize(output))
    ) {
      items.push({
        type: 'agent',
        id: `output:${run.id}`,
        runId: run.id,
        agentName: input.agentName,
        content: output,
        streaming: false,
        offset: output.length,
        reasoningOffset: 0,
        at: run.updatedAt,
      });
    }

  }

  for (const clarification of input.clarifications ?? []) {
    items.push({
      type: 'clarification',
      id: `clarification:${clarification.id}`,
      runId: clarification.runId,
      question: clarification.question,
      answer: clarification.answer ?? undefined,
      status: clarification.status,
      at: clarification.askedAt,
    });
  }

  return items.sort((a, b) => ts(a.at) - ts(b.at));
}

/** Envelope published by the API's agent-stream SSE (verbatim from the worker). */
export interface AgentStreamEnvelope {
  runId: string;
  agentId: string;
  agentName?: string;
  seq: number;
  events: Array<Record<string, unknown>>;
}

export interface ApplyStreamResult {
  items: ThreadItem[];
  /** True when a delta gap was detected and the thread should resync from run_steps. */
  needsResync: boolean;
}

/**
 * Applies one agent-stream envelope to the thread. Deltas append to the
 * matching in-progress agent message (created on first delta); offsets guard
 * against duplicated or missed payloads  -  a gap flags the message for resync
 * once it completes.
 */
export function applyAgentStreamEnvelope(
  items: ThreadItem[],
  envelope: AgentStreamEnvelope,
): ApplyStreamResult {
  let next = items;
  let needsResync = false;
  const now = new Date().toISOString();

  const findAgentItem = (messageKey: string): ThreadAgentMessage | undefined =>
    next.find(
      (item): item is ThreadAgentMessage =>
        item.type === 'agent' && item.id === `stream:${envelope.runId}:${messageKey}`,
    );

  for (const event of envelope.events) {
    const kind = String(event['event'] ?? '');

    if (kind === 'text-delta' || kind === 'reasoning-delta') {
      const messageKey = String(event['messageKey'] ?? '');
      const delta = String(event['delta'] ?? '');
      const offset = Number(event['offset'] ?? 0);
      let item = findAgentItem(messageKey);
      if (!item) {
        item = {
          type: 'agent',
          id: `stream:${envelope.runId}:${messageKey}`,
          runId: envelope.runId,
          agentName: envelope.agentName,
          content: '',
          streaming: true,
          offset: 0,
          reasoningOffset: 0,
          at: now,
        };
        next = [...next, item];
      }
      const updated: ThreadAgentMessage = { ...item };
      if (kind === 'text-delta') {
        if (offset === updated.offset) {
          updated.content += delta;
          updated.offset += delta.length;
        } else if (offset > updated.offset) {
          updated.gap = true;
          updated.content += delta;
          updated.offset = offset + delta.length;
        } else if (offset + delta.length > updated.offset) {
          // Partial overlap  -  append only the unseen suffix.
          const unseen = delta.slice(updated.offset - offset);
          updated.content += unseen;
          updated.offset += unseen.length;
        }
      } else {
        const current = updated.reasoning ?? '';
        if (offset === updated.reasoningOffset) {
          updated.reasoning = current + delta;
          updated.reasoningOffset += delta.length;
        } else if (offset > updated.reasoningOffset) {
          updated.gap = true;
          updated.reasoning = current + delta;
          updated.reasoningOffset = offset + delta.length;
        } else if (offset + delta.length > updated.reasoningOffset) {
          const unseen = delta.slice(updated.reasoningOffset - offset);
          updated.reasoning = current + unseen;
          updated.reasoningOffset += unseen.length;
        }
      }
      next = next.map((existing) => (existing.id === updated.id ? updated : existing));
    } else if (kind === 'tool-call') {
      const toolCallId = String(event['toolCallId'] ?? '');
      if (!toolCallId || next.some((item) => item.type === 'tool' && item.id === toolCallId)) continue;
      next = [
        ...next,
        {
          type: 'tool',
          id: toolCallId,
          runId: envelope.runId,
          agentName: envelope.agentName,
          toolName: String(event['toolName'] ?? 'tool'),
          input: event['input'],
          truncated: event['truncated'] === true,
          pending: true,
          at: now,
        },
      ];
    } else if (kind === 'tool-result') {
      const toolCallId = String(event['toolCallId'] ?? '');
      const output = String(event['output'] ?? '');
      const existing = next.find(
        (item): item is ThreadToolCall => item.type === 'tool' && item.id === toolCallId,
      );
      if (existing) {
        next = next.map((item) =>
          item.id === toolCallId && item.type === 'tool'
            ? { ...item, output, pending: false, truncated: item.truncated || event['truncated'] === true }
            : item,
        );
      } else if (toolCallId) {
        next = [
          ...next,
          {
            type: 'tool',
            id: toolCallId,
            runId: envelope.runId,
            agentName: envelope.agentName,
            toolName: String(event['toolName'] ?? 'tool'),
            input: undefined,
            output,
            truncated: event['truncated'] === true,
            pending: false,
            at: now,
          },
        ];
      }
    } else if (kind === 'message-complete') {
      const messageKey = String(event['messageKey'] ?? '');
      const item = findAgentItem(messageKey);
      if (item) {
        if (item.gap) needsResync = true;
        next = next.map((existing) =>
          existing.id === item.id && existing.type === 'agent'
            ? { ...existing, streaming: false }
            : existing,
        );
      }
    }
  }

  return { items: next, needsResync };
}

/**
 * After a rebuild from persisted history, carries over live items the durable
 * trace can't know about yet: in-flight streaming messages and tool calls /
 * gates / markers that aren't represented in the rebuilt thread.
 */
export function mergeCarryOverStreaming(rebuilt: ThreadItem[], previous: ThreadItem[]): ThreadItem[] {
  const rebuiltToolIds = new Set(rebuilt.filter((i) => i.type === 'tool').map((i) => i.id));
  const rebuiltIds = new Set(rebuilt.map((i) => i.id));
  const carried = previous.filter((item) => {
    if (item.type === 'agent') return item.streaming && !item.gap;
    if (item.type === 'tool') return item.pending && !rebuiltToolIds.has(item.id);
    if (item.type === 'marker' || item.type === 'gate' || item.type === 'clarification') {
      return !rebuiltIds.has(item.id);
    }
    return false;
  });
  return [...rebuilt, ...carried].sort((a, b) => ts(a.at) - ts(b.at));
}

/** Appends a marker unless one with the same id already exists. */
export function appendMarker(items: ThreadItem[], marker: ThreadMarker): ThreadItem[] {
  if (items.some((item) => item.id === marker.id)) return items;
  return [...items, marker];
}

/** Inserts or updates a clarification item in place. */
export function upsertClarification(
  items: ThreadItem[],
  clarification: ThreadClarification,
): ThreadItem[] {
  const existing = items.find((item) => item.id === clarification.id);
  if (!existing) return [...items, clarification];
  return items.map((item) => (item.id === clarification.id ? clarification : item));
}

/** Inserts or updates a human gate (approval / review) item in place. */
export function upsertGate(items: ThreadItem[], gate: ThreadGate): ThreadItem[] {
  const existing = items.find((item) => item.id === gate.id);
  if (!existing) return [...items, gate];
  return items.map((item) => (item.id === gate.id ? gate : item));
}

/** Minimal network-node shape needed to nest child-run activity under a subagent. */
export interface ThreadNetworkNode {
  delegationId: string;
  toAgentId: string;
  toAgentName?: string;
  childRunId?: string | null;
  status: string;
  childStatus?: string;
}

/** Leaf rows after consecutive-tool collapsing (root or inside a subagent). */
export type ThreadDisplayLeaf =
  | { kind: 'item'; item: ThreadItem }
  | {
      kind: 'tool-group';
      id: string;
      pending: boolean;
      /** First tool timestamp  -  keeps chronological placement. */
      at: string;
      tools: ThreadToolCall[];
    };

export type ThreadDisplayItem =
  | ThreadDisplayLeaf
  | {
      kind: 'subagent';
      id: string;
      agentName: string;
      childRunId: string;
      pending: boolean;
      /** First nested item timestamp  -  keeps chronological placement. */
      at: string;
      /** Raw nested items (pre tool-group collapse). */
      items: ThreadItem[];
      /** Nested items after consecutive-tool collapsing  -  what the UI renders. */
      children: ThreadDisplayLeaf[];
    };

/**
 * Collapse runs of 2+ consecutive tool items into a single `tool-group`.
 * A lone tool stays as `{ kind: 'item' }`. Non-tool items break the run.
 */
export function groupConsecutiveTools(items: ThreadItem[]): ThreadDisplayLeaf[] {
  const out: ThreadDisplayLeaf[] = [];
  let run: ThreadToolCall[] = [];

  const flush = () => {
    if (!run.length) return;
    if (run.length === 1) {
      out.push({ kind: 'item', item: run[0] });
    } else {
      out.push({
        kind: 'tool-group',
        id: `tools:${run[0].id}:${run[run.length - 1].id}`,
        pending: run.some((t) => t.pending),
        at: run[0].at,
        tools: run,
      });
    }
    run = [];
  };

  for (const item of items) {
    if (item.type === 'tool') {
      run.push(item);
    } else {
      flush();
      out.push({ kind: 'item', item });
    }
  }
  flush();
  return out;
}

const ACTIVE_CHILD_STATUSES = new Set([
  'queued',
  'working',
  'running',
  // Human-wait states: keep the subagent row expanded / "running…" so operators
  // notice the child is blocked (gates themselves render at the root).
  'waiting_approval',
  'waiting_review',
  'waiting_input',
  'escalated',
]);

function isChildPending(node: ThreadNetworkNode): boolean {
  const status = node.childStatus ?? node.status;
  return ACTIVE_CHILD_STATUSES.has(status);
}

function itemRunId(item: ThreadItem): string | undefined {
  if (item.type === 'user' || item.type === 'marker') return undefined;
  return item.runId;
}

/** Human-action items stay at the root so Approve/Reject/Answer never hide in a collapsed subagent. */
function staysAtRoot(item: ThreadItem): boolean {
  return item.type === 'gate' || item.type === 'clarification';
}

/**
 * Groups child-run thread items under collapsible subagent rows using
 * delegation network nodes. Root-run items stay at the top level.
 * Items whose runId is not a known child stay at root (including orphan child
 * activity before the network snapshot arrives).
 *
 * After nesting, consecutive tool calls (root and inside subagents) collapse
 * into Cursor-style `tool-group` rows.
 */
export function groupThreadForDisplay(
  items: ThreadItem[],
  nodes: ThreadNetworkNode[],
): ThreadDisplayItem[] {
  const childNodes = nodes.filter(
    (node): node is ThreadNetworkNode & { childRunId: string } => Boolean(node.childRunId),
  );
  if (!childNodes.length) {
    return groupConsecutiveTools(items);
  }

  const nodeByChildRun = new Map(childNodes.map((node) => [node.childRunId, node]));
  const childRunIds = new Set(nodeByChildRun.keys());

  const nestedByChild = new Map<string, ThreadItem[]>();
  const root: ThreadItem[] = [];

  for (const item of items) {
    const runId = itemRunId(item);
    if (runId && childRunIds.has(runId) && !staysAtRoot(item)) {
      const bucket = nestedByChild.get(runId) ?? [];
      bucket.push(item);
      nestedByChild.set(runId, bucket);
    } else {
      root.push(item);
    }
  }

  // Emit subagent groups at the position of their first nested item's timestamp
  // among root items (Cursor-like: group appears when that agent starts working).
  const groups = childNodes
    .map((node) => {
      const nested = nestedByChild.get(node.childRunId) ?? [];
      if (!nested.length) return null;
      const at = nested[0]?.at ?? new Date(0).toISOString();
      return {
        kind: 'subagent' as const,
        id: `subagent:${node.delegationId}`,
        agentName: node.toAgentName || node.toAgentId.slice(0, 8),
        childRunId: node.childRunId,
        pending:
          isChildPending(node) ||
          nested.some(
            (i) => (i.type === 'tool' && i.pending) || (i.type === 'agent' && i.streaming),
          ),
        at,
        items: nested,
        children: groupConsecutiveTools(nested),
      };
    })
    .filter((g): g is NonNullable<typeof g> => g !== null);

  const display: Array<{ kind: 'item'; item: ThreadItem } | (typeof groups)[number]> = [
    ...root.map((item) => ({ kind: 'item' as const, item })),
    ...groups,
  ];

  const sorted = display.sort((a, b) => {
    const aAt = a.kind === 'item' ? a.item.at : a.at;
    const bAt = b.kind === 'item' ? b.item.at : b.at;
    return ts(aAt) - ts(bAt);
  });

  // Collapse consecutive root-level tools; subagent rows stay as separators.
  const out: ThreadDisplayItem[] = [];
  let batch: ThreadItem[] = [];
  const flushBatch = () => {
    if (!batch.length) return;
    out.push(...groupConsecutiveTools(batch));
    batch = [];
  };
  for (const entry of sorted) {
    if (entry.kind === 'item') {
      batch.push(entry.item);
    } else {
      flushBatch();
      out.push(entry);
    }
  }
  flushBatch();
  return out;
}
